import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { FadeUp } from "@/components/landing/Reveal";
import Seo from "@/components/Seo";

const PLATE_I =
	"https://customer-assets.emergentagent.com/job_enterprise-ai-pitch/artifacts/hi7y4hz5_annie-spratt-QckxruozjRg-unsplash.jpg";
const PLATE_II =
	"https://customer-assets.emergentagent.com/job_enterprise-ai-pitch/artifacts/k7d0sqf2_museums-victoria-7YUvAUbfSV0-unsplash.jpg";

const META = [
	["Published", "2026"],
	["Author", "Natant AI Labs · Research"],
	["Length", "≈ 14 min read"],
	["Sections", "11 + Refs"],
];

const SECTIONS = [
	{
		num: "01",
		id: "section-01",
		title: "The current AI narrative is underestimating the real opportunity.",
		blocks: [
			{ t: "p", text: "The market's attention today is concentrated around foundational models, model performance races, GPU infrastructure, consumer AI products, and productivity assistants. This is understandable. Consumer AI is visible; enterprise transformation is not." },
			{ t: "p", text: "Yet history consistently demonstrates that the largest technological value creation happens after infrastructure matures — not during the infrastructure race itself." },
			{ t: "h3", text: "Historical Parallel" },
			{ t: "p", text: "The internet itself was revolutionary, but the largest enterprise value emerged later through cloud infrastructure, SaaS, marketplaces, logistics platforms, digital commerce, and operational ecosystems. Similarly:" },
			{ t: "list", items: ["mobile computing did not create the most value at the hardware layer,", "cloud did not create the most value at the virtualization layer,", "and the web did not create the most value at the protocol layer."] },
			{ t: "p", text: "The largest outcomes emerged from workflow transformation, ecosystem orchestration, and operational reinvention. AI is following the same trajectory." },
		],
	},
	{
		num: "02",
		id: "section-02",
		title: "Enterprise software was built for a different world.",
		blocks: [
			{ t: "p", text: "Over the last thirty years, enterprises digitized themselves through fragmented software stacks — ERP, CRM, marketing suites, POS, supply chain tools, HR systems, WMS, finance systems, analytics layers, and customer support platforms." },
			{ t: "p", text: "These systems were designed for record keeping, transactional processing, manual coordination, and human-led workflows. But modern businesses no longer operate in predictable environments. They operate in real-time markets, omnichannel ecosystems, fragmented customer journeys, supply chain volatility, hyper-personalized expectations, and continuous operational complexity." },
			{ t: "quote", text: "Businesses today run on fragmented systems — ERPs, CRMs, POS, supply chain tools, marketing platforms, customer support stacks — none of them truly talk to each other, none of them learn from each other.", cite: "The Pitch: Rebuilding the Operating System for Business" },
			{ t: "p", text: "This fragmentation has created operational silos, duplicated workflows, disconnected customer experiences, poor visibility, and enormous hidden inefficiencies. The enterprise stack today resembles a digital nervous system without intelligence. AI changes this fundamentally." },
		],
	},
	{
		num: "03",
		id: "section-03",
		title: "AI is not another application layer — it is the coordination layer.",
		blocks: [
			{ t: "p", text: "Most technology waves added new software categories. AI is different. AI changes how software behaves, how decisions are made, how workflows adapt, and how enterprises coordinate themselves. This is why AI should not be viewed as “another SaaS category.” AI represents the emergence of systems of intelligence." },
			{ t: "p", text: "Historically, software stored information, humans interpreted information, and humans executed workflows. AI collapses these layers. The enterprise stack is evolving:" },
			{
				t: "table",
				head: ["Traditional Enterprise", "AI-Native Enterprise"],
				rows: [
					["Systems of Record", "Systems of Intelligence"],
					["Dashboards", "Autonomous Reasoning"],
					["Manual Coordination", "Agentic Coordination"],
					["Static Workflows", "Adaptive Workflows"],
					["Departmental Silos", "Cross-Functional Intelligence"],
					["Reactive Operations", "Predictive Operations"],
					["Reporting", "Continuous Optimization"],
				],
			},
			{ t: "p", text: "This transition is comparable to mainframe → PC, on-premise → cloud, monolith → composable architectures. Only this time, the transformation is cognitive." },
		],
	},
	{
		num: "04",
		id: "section-04",
		title: "Why Enterprise AI is larger than Consumer AI.",
		blocks: [
			{ t: "quote", text: "Consumer AI creates awareness. Enterprise AI creates economic transformation." },
			{ t: "p", text: "The enterprise software market already exceeds hundreds of billions of dollars annually. But Enterprise AI expands far beyond software budgets. It attacks operational inefficiencies, labor coordination, process management, consulting dependency, support operations, workflow orchestration, and decision infrastructure itself." },
			{ t: "p", text: "Traditional SaaS monetized software access. Enterprise AI monetizes operational outcomes. That distinction changes the total addressable market dramatically." },
			{
				t: "table",
				head: ["SaaS Era", "AI-Native Era"],
				rows: [
					["CRM", "Revenue Intelligence System"],
					["Marketing Automation", "Autonomous Growth Engine"],
					["ERP Dashboard", "Operational Intelligence Layer"],
					["Customer Support Software", "AI Resolution Systems"],
					["BI Reporting", "Strategic Decision Systems"],
					["Workflow Tools", "Autonomous Execution Engines"],
				],
			},
			{ t: "p", text: "Enterprise AI captures value from software, services, labor, and operational optimization simultaneously. This market is measured in trillions." },
		],
	},
	{
		num: "05",
		id: "section-05",
		title: "Composable commerce was an early signal of the shift.",
		blocks: [
			{ t: "p", text: "One of the earliest indicators of this transition emerged through composable commerce architectures. Composable commerce challenged traditional monolithic systems by emphasizing modularity, APIs, orchestration, interoperability, and dynamic experiences." },
			{ t: "quote", text: "Customer experiences are no longer linear." },
			{ t: "p", text: "Modern commerce journeys became contextual, omnichannel, adaptive, and continuously evolving. Composable architectures enabled interoperability between systems, flexible orchestration, and modular enterprise capabilities." },
			{ t: "p", text: "AI is the natural evolution of this movement. Composable commerce solved modularity. Enterprise AI solves cognition." },
			{ t: "h3", text: "Intelligent Orchestration" },
			{ t: "p", text: "Where systems understand context, communicate autonomously, coordinate workflows, and continuously optimize outcomes." },
		],
	},
	{
		num: "06",
		id: "section-06",
		title: "The rise of the intelligent enterprise.",
		blocks: [
			{ t: "p", text: "The future enterprise will not simply be “digitized.” It will become continuously intelligent. This changes the role of enterprise software entirely. Instead of isolated applications, enterprises will operate as interconnected intelligence networks." },
			{ t: "p", text: "Characteristics of AI-native enterprises include:" },
			{ t: "list", items: ["AI-assisted operations,", "autonomous coordination,", "predictive workflows,", "real-time optimization,", "adaptive customer experiences,", "and continuously improving operational systems."] },
			{ t: "p", text: "This transition affects every industry — retail, logistics, manufacturing, healthcare, aviation, hospitality, government, finance, real estate, and education." },
			{ t: "image", src: PLATE_II, alt: "An archival diagram — interconnected systems mapped by hand.", caption: "The enterprise as a nervous system — interconnected, intelligent, continuously improving.", plate: "Plate II · Museums Victoria" },
		],
	},
	{
		num: "07",
		id: "section-07",
		title: "Enterprise data is the new strategic advantage.",
		blocks: [
			{ t: "p", text: "The long-term winners in Enterprise AI will not necessarily possess the largest models. They will possess the deepest operational context." },
			{ t: "p", text: "Enterprise environments contain transactional histories, customer behavior, operational workflows, logistics events, supply-chain intelligence, workforce patterns, procurement activities, and support interactions. This creates enterprise memory." },
			{ t: "p", text: "AI systems deeply embedded into workflows become increasingly valuable over time because they accumulate contextual understanding, operational nuance, and organizational intelligence. This creates defensibility far stronger than models alone." },
			{ t: "quote", text: "The moat is not the AI model. The moat is workflow integration, enterprise context, and operational intelligence." },
		],
	},
	{
		num: "08",
		id: "section-08",
		title: "Why the Middle East has a unique strategic position.",
		blocks: [
			{ t: "p", text: "One of the most overlooked dimensions of the AI transition is geography. The Middle East — particularly the GCC — possesses structural advantages for Enterprise AI adoption." },
			{ t: "quote", text: "Sovereign capital, aggressive digitization, greenfield infrastructure, and relatively lower legacy lock-in create unique advantages.", cite: "Enterprise Operating System Thesis" },
			{ t: "p", text: "Unlike mature Western markets burdened by decades of technical debt, many GCC ecosystems are still in active transformation phases. This creates an opportunity to leapfrog legacy architectures, adopt AI-native systems directly, and redesign enterprise ecosystems from the ground up — particularly across smart cities, aviation, logistics, retail, hospitality, government ecosystems, healthcare, and free-zone economies." },
			{ t: "p", text: "The GCC may become the global proving ground for AI-native enterprise infrastructure." },
		],
	},
	{
		num: "09",
		id: "section-09",
		title: "The emergence of AI-native business operating systems.",
		blocks: [
			{ t: "p", text: "The long-term convergence is increasingly clear. Enterprise systems are evolving toward unified AI operating systems — not monolithic ERP replacements, but intelligence layers sitting above fragmented enterprise infrastructure." },
			{ t: "p", text: "These systems will orchestrate workflows, unify enterprise context, coordinate decisions, automate execution, and continuously optimize operations." },
			{ t: "quote", text: "The entire business becomes intelligent, interconnected, and continuously improving on its own.", cite: "Enterprise OS Thesis" },
			{ t: "p", text: "This is not simply software evolution. It is the emergence of the intelligent enterprise." },
		],
	},
	{
		num: "10",
		id: "section-10",
		title: "Why this opportunity is still early.",
		blocks: [
			{ t: "p", text: "Despite massive excitement around AI, the enterprise market remains in the earliest innings. Today most AI deployments remain experimental; copilots are largely productivity tools; integrations are shallow; and enterprise orchestration remains fragmented." },
			{ t: "p", text: "The market is still operating at the interface layer of AI. The real transformation occurs at workflow orchestration, enterprise cognition, and operational autonomy." },
			{ t: "p", text: "This transition may take ten to fifteen years fully — but the foundational infrastructure is being established now. It resembles cloud computing in the late 2000s, mobile ecosystems before app economies exploded, or the internet before digital commerce matured. The largest value creation is still ahead." },
		],
	},
	{
		num: "11",
		id: "section-11",
		title: "Strategic implications for startups.",
		blocks: [
			{ t: "p", text: "The next generation of AI companies should not think merely in terms of AI applications. They should think in terms of enterprise orchestration, operational systems, workflow intelligence, and business cognition." },
			{ t: "p", text: "The most valuable companies will likely:" },
			{ t: "list", items: ["integrate deeply into enterprise workflows,", "own operational context,", "unify fragmented systems,", "and evolve into AI coordination layers."] },
			{ t: "p", text: "This creates opportunities across:" },
			{ t: "list", items: ["vertical AI systems,", "AI-native ERP layers,", "intelligent commerce,", "logistics orchestration,", "AI operations,", "enterprise memory systems,", "and autonomous workflow infrastructure."] },
			{ t: "quote", text: "The winners may resemble operating systems more than applications." },
		],
	},
	{
		num: "12",
		id: "section-12",
		title: "Conclusion.",
		blocks: [
			{ t: "p", text: "The current AI boom is often framed around content generation, assistants, and productivity enhancement. But these represent only the first visible phase of a much larger transformation." },
			{ t: "p", text: "The real economic opportunity lies in rebuilding how enterprises operate." },
			{ t: "p", text: "Enterprise AI is not merely a software upgrade. It is the convergence of software, operations, workflows, decision-making, and intelligence into a unified adaptive system." },
			{ t: "quote", text: "The next decade will not simply produce AI-powered applications. It will produce AI-native enterprises. And the companies that enable this transition will define the next era of global business infrastructure." },
		],
	},
];

const REFERENCES = [
	["01", "The Pitch: We're Rebuilding the Operating System for Business — From the Ground Up.", null],
	["02", "Composable Commerce & Seamless Talk — Digitalworks.", null],
	["03", "Sequoia Capital — AI market infrastructure and application theses", "https://www.sequoiacap.com/"],
	["04", "McKinsey Global Institute — Generative AI economic impact reports", "https://www.mckinsey.com/mgi"],
	["05", "NVIDIA — Enterprise AI infrastructure research", "https://www.nvidia.com/en-us/ai-data-science/"],
	["06", "Gartner — Future of enterprise applications and AI orchestration", "https://www.gartner.com/"],
	["07", "Andreessen Horowitz — AI application layer analyses", "https://a16z.com/"],
	["08", "Microsoft & OpenAI — Enterprise deployment trends", "https://openai.com/customer-stories/"],
];

function Block({ block }) {
	if (block.t === "p") {
		return <p className="text-base md:text-lg leading-relaxed text-[var(--bone-dim)] max-w-[68ch]">{block.text}</p>;
	}
	if (block.t === "h3") {
		return <h3 className="font-display text-xl md:text-2xl font-semibold tracking-[-0.02em] pt-4">{block.text}</h3>;
	}
	if (block.t === "quote") {
		return (
			<blockquote className="border-l-2 border-[var(--accent-green)] pl-6 md:pl-8 py-2 my-2">
				<p className="font-editorial italic text-2xl md:text-[32px] leading-[1.25] text-[var(--bone)] max-w-[30ch]">
					{block.text}
				</p>
				{block.cite && (
					<cite className="block mt-4 not-italic text-xs text-[var(--bone-dim)] font-medium tracking-wide">
						— {block.cite}
					</cite>
				)}
			</blockquote>
		);
	}
	if (block.t === "list") {
		return (
			<ul className="space-y-2 max-w-[68ch]">
				{block.items.map((item) => (
					<li key={item} className="flex items-start gap-3 text-base md:text-lg leading-relaxed text-[var(--bone-dim)]">
						<span className="mt-[0.65em] w-1.5 h-1.5 rounded-full bg-[var(--accent-green)] shrink-0" />
						<span>{item}</span>
					</li>
				))}
			</ul>
		);
	}
	if (block.t === "table") {
		return (
			<div className="overflow-x-auto">
				<table className="w-full border border-[var(--border-line)] text-sm md:text-base" data-testid="whitepaper-table">
					<thead>
						<tr className="bg-[var(--surface)]">
							{block.head.map((h) => (
								<th key={h} className="text-left font-display font-semibold text-[var(--bone)] px-5 py-3.5 border-b border-r last:border-r-0 border-[var(--border-line)]">
									{h}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{block.rows.map((row) => (
							<tr key={row[0]}>
								<td className="px-5 py-3 text-[var(--bone-dim)] border-b border-r border-[var(--border-line)]">{row[0]}</td>
								<td className="px-5 py-3 text-[var(--bone)] font-medium border-b border-[var(--border-line)]">{row[1]}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		);
	}
	if (block.t === "image") {
		return (
			<figure className="my-4">
				<div className="clip-frame aspect-[16/9] border border-[var(--border-line)]">
					<img src={block.src} alt={block.alt} className="w-full h-full object-cover" />
				</div>
				<figcaption className="mt-3 flex items-baseline justify-between gap-4 text-xs text-[var(--bone-dim)]">
					<span className="font-editorial italic text-sm">{block.caption}</span>
					<span className="shrink-0 font-medium">{block.plate}</span>
				</figcaption>
			</figure>
		);
	}
	return null;
}

export default function WhitepaperPage() {
	const { hash } = useLocation();

	useEffect(() => {
		if (hash) {
			const el = document.getElementById(hash.slice(1));
			if (el) {
				el.scrollIntoView({ behavior: "smooth", block: "start" });
				return;
			}
		}
		window.scrollTo(0, 0);
	}, [hash]);

	return (
		<div data-testid="whitepaper-page" className="min-h-screen bg-[var(--obsidian)] text-[var(--bone)]">
			<Seo title="The Intelligent Enterprise — Natant Whitepaper 01" description="A working note on the emergence of AI-native business operating systems — and why enterprise AI will become the largest economic opportunity of the next decade." path="/whitepaper" />

			{/* Slim header */}
			<header className="border-b border-[var(--border-line)]">
				<div className="max-container flex items-center justify-between h-[64px]">
					<Link to="/" className="flex items-center gap-2.5">
						<span className="font-display text-[19px] tracking-[-0.02em] font-semibold whitespace-nowrap">Natant <span className="wordmark-ai">AI</span> Labs<span className="text-[var(--accent-green)]">.</span></span>
					</Link>
					<div className="eyebrow hidden md:block">Natant AI Labs · Whitepaper · 01</div>
					<Link
						to="/"
						data-testid="whitepaper-back-link"
						className="text-sm text-[var(--bone-dim)] hover:text-[var(--bone)] transition-colors font-medium"
					>
						← Back to site
					</Link>
				</div>
			</header>

			<main className="max-container">
				{/* Preamble */}
				<section className="py-16 md:py-24 border-b border-[var(--border-line)]">
					<div className="eyebrow mb-6">Preamble</div>
					<h1 className="hm-h1 text-[10vw] md:text-[5.2vw] leading-[1.02] max-w-[16ch] mb-8" data-testid="whitepaper-title">
						The <em className="em">Intelligent</em> Enterprise
					</h1>
					<p className="text-xl md:text-2xl leading-snug text-[var(--bone)] max-w-[46ch] font-medium mb-4">
						Why Enterprise AI Will Become the Largest Economic Opportunity of the Next Decade.
					</p>
					<p className="text-base md:text-lg text-[var(--bone-dim)] max-w-[52ch] mb-14">
						A whitepaper on the emergence of AI-native business operating systems.
					</p>

					<div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-[var(--border-line)] mb-14">
						{META.map(([label, value]) => (
							<div key={label} className="border-r border-b border-[var(--border-line)] p-5 md:p-6">
								<div className="eyebrow mb-2">{label}</div>
								<div className="font-display text-base md:text-lg font-medium">{value}</div>
							</div>
						))}
					</div>

					<figure>
						<div className="clip-frame aspect-[16/8] border border-[var(--border-line)]">
							<img src={PLATE_I} alt="A still life of an unfinished workbench — pages, drafts, working notes." className="w-full h-full object-cover" />
						</div>
						<figcaption className="mt-3 flex items-baseline justify-between gap-4 text-xs text-[var(--bone-dim)]">
							<span className="font-editorial italic text-sm">
								A working note, still in long-hand. The next decade of business is being drafted in the margins.
							</span>
							<span className="shrink-0 font-medium">Plate I · Annie Spratt</span>
						</figcaption>
					</figure>
				</section>

				{/* Abstract */}
				<section className="py-16 md:py-20 border-b border-[var(--border-line)]">
					<div className="eyebrow mb-8">Abstract</div>
					<div className="space-y-6">
						<p className="text-base md:text-lg leading-relaxed text-[var(--bone-dim)] max-w-[68ch]">
							The world is currently experiencing the fastest technological acceleration since the rise of the internet.
						</p>
						<p className="text-base md:text-lg leading-relaxed text-[var(--bone-dim)] max-w-[68ch]">
							Artificial Intelligence has rapidly entered mainstream consciousness through conversational AI, generative
							content, copilots, code generation, media synthesis, and consumer-facing assistants.
						</p>
						<p className="text-base md:text-lg leading-relaxed text-[var(--bone-dim)] max-w-[68ch]">
							However, the dominant market narrative around AI remains fundamentally incomplete. Most of today's discourse
							focuses on models, interfaces, benchmarks, and consumer applications. But the largest economic opportunity in
							AI will not emerge from chat interfaces alone — it will emerge from the complete reinvention of enterprise
							operations.
						</p>
						<blockquote className="border-l-2 border-[var(--accent-green)] pl-6 md:pl-8 py-2">
							<p className="font-editorial italic text-2xl md:text-[32px] leading-[1.25] text-[var(--bone)] max-w-[30ch]">
								Enterprise AI represents the most significant business infrastructure shift since cloud computing — and
								potentially since the emergence of enterprise software itself.
							</p>
						</blockquote>
						<p className="text-base md:text-lg leading-relaxed text-[var(--bone-dim)] max-w-[68ch]">
							The next decade will witness the transformation of enterprises from fragmented digital systems, siloed
							workflows, and reactive operations into intelligent, adaptive, continuously optimizing, AI-native
							organizations. AI is not merely becoming another software category. AI is becoming the orchestration layer of
							the modern enterprise.
						</p>
					</div>
				</section>

				{/* Sections 01–12 */}
				{SECTIONS.map((s) => (
					<section key={s.id} id={s.id} data-testid="whitepaper-section" className="py-14 md:py-20 border-b border-[var(--border-line)] scroll-mt-20">
						<FadeUp>
							<div className="grid grid-cols-12 gap-6 mb-8">
								<div className="col-span-12 md:col-span-2">
									<span className="chapter-number">{s.num}</span>
								</div>
								<h2 className="col-span-12 md:col-span-10 font-display text-2xl md:text-4xl font-semibold tracking-[-0.02em] leading-[1.15] max-w-[24ch]">
									{s.title}
								</h2>
							</div>
							<div className="grid grid-cols-12 gap-6">
								<div className="col-span-12 md:col-span-10 md:col-start-3 space-y-6">
									{s.blocks.map((b, i) => (
										<Block key={i} block={b} />
									))}
								</div>
							</div>
						</FadeUp>
					</section>
				))}

				{/* References */}
				<section className="py-14 md:py-20 border-b border-[var(--border-line)]">
					<div className="eyebrow mb-8">References</div>
					<ol className="space-y-3 max-w-[72ch]">
						{REFERENCES.map(([num, text, href]) => (
							<li key={num} className="flex items-baseline gap-4 text-sm md:text-base text-[var(--bone-dim)]">
								<span className="font-mono text-[11px] tracking-[0.18em] shrink-0">[{num}]</span>
								{href ? (
									<a href={href} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--bone)] transition-colors underline underline-offset-4">
										{text} ↗
									</a>
								) : (
									<span>{text}</span>
								)}
							</li>
						))}
					</ol>
				</section>

				{/* Closing CTA */}
				<section className="py-16 md:py-24 text-center">
					<p className="font-editorial italic text-2xl md:text-3xl text-[var(--bone)] max-w-[36ch] mx-auto mb-10 leading-[1.3]">
						The next decade of business will be written by the companies that learn to think with machines — without
						losing the thread of judgment.
					</p>
					<div className="flex flex-wrap items-center justify-center gap-3">
						<Link
							to="/apply"
							data-testid="whitepaper-apply-cta"
							className="pill-btn pill-btn--solid"
							style={{ background: "var(--accent-green)", borderColor: "var(--accent-green)", color: "var(--obsidian)" }}
						>
							Apply for Cohort 1 →
						</Link>
						<Link to="/" className="pill-btn">
							Return home
						</Link>
					</div>
					<div className="mt-16 eyebrow">Natant AI Labs · 2026</div>
				</section>
			</main>
		</div>
	);
}
